/**
 * API Client for TradeRewind Backend
 * Handles all HTTP requests to the backend API
 */

// Token getter function - will be set by the app when Clerk is initialized
let getAuthToken: (() => Promise<string | null>) | null = null;

export const setAuthTokenGetter = (getter: () => Promise<string | null>) => {
  getAuthToken = getter;
};

// Get influencer data from localStorage (for 100% discount check)
const getInfluencerData = (): { slug: string | null; discountCode: string | null } => {
  try {
    const stored = localStorage.getItem('tradevision_influencer');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        slug: parsed?.slug || null,
        discountCode: parsed?.specialOffer?.discountCode || null,
      };
    }
  } catch {
    // Ignore parse errors
  }
  return { slug: null, discountCode: null };
};

// Auto-detect protocol to avoid mixed content issues
export const getApiBaseUrl = () => {
  // In development, use empty string to leverage Vite's proxy
  // Vite proxy is configured to forward /api/* to backend
  if (import.meta.env.DEV) {
    return ''; // Use relative URLs, Vite proxy handles routing
  }
  
  // If VITE_API_URL is set (production), use it
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Otherwise, use same protocol as frontend to avoid mixed content
  const protocol = window.location.protocol; // 'http:' or 'https:'
  const host = window.location.hostname;
  const port = import.meta.env.VITE_API_PORT || '3000';
  
  // If on localhost, use the port
  if (host === 'localhost' || host === '127.0.0.1') {
    return `${protocol}//${host}:${port}`;
  }
  
  // For production, assume API is on same host
  return `${protocol}//${host}`;
};

export const API_BASE_URL = getApiBaseUrl();
console.log('[API] Base URL:', API_BASE_URL || '(using Vite proxy)');

interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: error.message || 'Request failed', message: error.reason };
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Authenticated request - includes Clerk auth token
  private async authenticatedRequest<T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    try {
      // Get auth token if available
      const token = getAuthToken ? await getAuthToken() : null;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      };

      // Add Authorization header if we have a token
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Add influencer headers for 100% discount check
      const influencerData = getInfluencerData();
      if (influencerData.slug) {
        headers['X-Influencer-Slug'] = influencerData.slug;
      }
      if (influencerData.discountCode) {
        headers['X-Discount-Code'] = influencerData.discountCode;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const error = await response.json();
        return { 
          error: error.error || error.message || 'Request failed',
          message: error.reason || error.requiredPlan ? `Requires ${error.requiredPlan} plan` : undefined
        };
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      console.error('API request failed:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Market Data API
  async getCandles(symbol: string, timeframe: string = '1h', limit: number = 100) {
    return this.request(`/api/market/candles/${symbol}?timeframe=${timeframe}&limit=${limit}`);
  }

  async getTicks(symbol: string, start?: number, end?: number) {
    const params = new URLSearchParams();
    if (start) params.append('start', start.toString());
    if (end) params.append('end', end.toString());
    return this.request(`/api/market/ticks/${symbol}?${params}`);
  }

  async getQuote(symbol: string) {
    return this.request(`/api/market/quote/${symbol}`);
  }

  // AI Analysis API (requires premium subscription)
  async analyzeImage(imageData: string, prompt?: string, symbol?: string, timeframe?: string) {
    return this.authenticatedRequest('/api/ai/analyze-image', {
      method: 'POST',
      body: JSON.stringify({ imageData, prompt, symbol, timeframe }),
    });
  }

  async analyzeData(data: any, type: string) {
    return this.authenticatedRequest('/api/ai/analyze-data', {
      method: 'POST',
      body: JSON.stringify({ data, type }),
    });
  }

  async chat(message: string, context?: any) {
    return this.authenticatedRequest('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ message, context }),
    });
  }

  // Replay Trading API
  async getReplaySessions() {
    return this.request('/api/replay/sessions');
  }

  async createReplaySession(symbol: string, startTime: number, endTime?: number) {
    return this.request('/api/replay/sessions', {
      method: 'POST',
      body: JSON.stringify({ symbol, startTime, endTime }),
    });
  }

  async getReplaySession(id: string) {
    return this.request(`/api/replay/sessions/${id}`);
  }

  async deleteReplaySession(id: string) {
    return this.request(`/api/replay/sessions/${id}`, {
      method: 'DELETE',
    });
  }

  // User API
  async getUserProfile() {
    return this.request('/api/user/profile');
  }

  async getUserSessions() {
    return this.request('/api/user/sessions');
  }

  async saveUserSession(sessionData: any) {
    return this.request('/api/user/sessions', {
      method: 'POST',
      body: JSON.stringify({ sessionData }),
    });
  }

  // Health Check
  async healthCheck() {
    return this.request('/api/health');
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);

// Export types
export type { ApiResponse };
